import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Export an invoice element to PDF by rendering a full off-screen clone.
 * This guarantees the entire element (including colored headers) is captured
 * regardless of scroll position or visible viewport.
 *
 * @param elementId - The ID of the element to export
 * @param fileName - The name of the PDF file (without extension)
 * @param returnBytes - If true, returns Uint8Array instead of triggering download
 */
export const exportInvoiceToPdf = async (
  elementId: string,
  fileName: string = 'invoice',
  returnBytes: boolean = false,
  scale: number = 1.2 // Default lightweight scale
): Promise<void | Uint8Array> => {
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error(`Element with ID "${elementId}" not found`);
  }

  // --- Build a full-size off-screen wrapper ---
  // We clone the node deeply so all child elements and their inline styles transfer.
  // The wrapper is positioned far off-screen so it never affects layout.
  const wrapper = document.createElement('div');
  wrapper.style.cssText = `
    position: fixed;
    top: 0;
    left: -9999px;
    width: ${element.scrollWidth}px;
    min-height: ${element.scrollHeight}px;
    background: #ffffff;
    z-index: -1;
    overflow: visible;
  `;

  const clone = element.cloneNode(true) as HTMLElement;
  // Force the clone to show its full height – no overflow clipping
  clone.style.cssText += `
    width: ${element.scrollWidth}px;
    min-height: ${element.scrollHeight}px;
    max-height: none;
    overflow: visible;
    position: static;
    transform: none;
  `;
  wrapper.appendChild(clone);
  document.body.appendChild(wrapper);

  // Brief wait for any async image loads / font rendering inside the clone
  await new Promise(resolve => setTimeout(resolve, 400));

  try {
    const canvas = await html2canvas(wrapper, {
      scale: scale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      scrollX: 0,
      scrollY: 0,
      width: wrapper.scrollWidth,
      height: wrapper.scrollHeight,
      windowWidth: wrapper.scrollWidth,
      windowHeight: wrapper.scrollHeight,
    });

    // A4 dimensions in mm
    const A4_W = 210;
    const A4_H = 297;
    const imgH = (canvas.height * A4_W) / canvas.width;

    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgData = canvas.toDataURL('image/jpeg', 0.75); // JPEG with 75% compression (massively reduces PDF file size)

    if (imgH <= A4_H) {
      pdf.addImage(imgData, 'JPEG', 0, 0, A4_W, imgH);
    } else {
      // Slice the canvas into A4 pages
      const pageHeightPx = Math.floor((canvas.width * A4_H) / A4_W);
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = canvas.width;
      let offsetPx = 0;
      let firstPage = true;

      while (offsetPx < canvas.height) {
        const sliceH = Math.min(pageHeightPx, canvas.height - offsetPx);
        if (sliceH <= 0) break;

        sliceCanvas.height = sliceH;
        const ctx = sliceCanvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, sliceCanvas.width, sliceH);
          ctx.drawImage(canvas, 0, offsetPx, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
        }

        const sliceHmm = (sliceH * A4_W) / canvas.width;
        if (!firstPage) pdf.addPage();
        pdf.addImage(sliceCanvas.toDataURL('image/jpeg', 0.75), 'JPEG', 0, 0, A4_W, sliceHmm);

        firstPage = false;
        offsetPx += sliceH;
      }
    }

    if (returnBytes) {
      return new Uint8Array(pdf.output('arraybuffer'));
    } else {
      pdf.save(`${fileName}.pdf`);
    }
  } finally {
    document.body.removeChild(wrapper);
  }
};
